/**
 * 5×5 Markov transition matrix and prior for biome sampling (ADR-0013 §3,
 * amended by HEX-118: distance-banded priors).
 *
 * Rows = the biome a cell *would take*; columns = the biome of a resolved
 * neighbour cell. For a cell with resolved-lower-level neighbours `n_1 … n_k`
 * the unnormalised probability of taking biome `b` is
 *
 *     prior[b] · ∏_i W[b][biome(n_i)]
 *
 * The matrix and prior are the locked starting weights from ADR-0013 §3 —
 * tunable, but moving them changes world feel so each retune lives behind
 * an ADR amendment.
 *
 * HEX-118 adds distance-banded priors: cells near origin are biased toward
 * tame biomes (plain/forest); cells deep in the frontier lean to variety. The
 * transition matrix is unchanged — only the unconditioned prior shifts by band.
 * HEX-132 retunes the outer band so frontier variety is carried by *passable*
 * snow rather than impassable lake/mountain (see `BIOME_PRIOR_OUTER`).
 */
import type { Biome } from '../core/tile';
import { hashCombine, mulberry32 } from './noise';

export type { Biome };
export const BIOMES: readonly Biome[] = ['plain', 'forest', 'lake', 'snow', 'mountain'];

export const TRANSITION_MATRIX: Readonly<Record<Biome, Readonly<Record<Biome, number>>>> = {
  plain: { plain: 3, forest: 3, lake: 3, snow: 2, mountain: 2 },
  forest: { plain: 3, forest: 4, lake: 2, snow: 1, mountain: 1 },
  lake: { plain: 2, forest: 2, lake: 4, snow: 1, mountain: 1 },
  snow: { plain: 1, forest: 1, lake: 1, snow: 4, mountain: 3 },
  mountain: { plain: 1, forest: 1, lake: 1, snow: 3, mountain: 4 },
};

/**
 * Distance thresholds (hexes from origin, measured at the cell's centre) for
 * the biome-prior bands (ADR-0013 amendment, HEX-118). Cells whose centres
 * are within `BIOME_PRIOR_NEAR_DIST` use the inner (tame) prior; cells beyond
 * `BIOME_PRIOR_FAR_DIST` use the outer (variety) prior; cells in between use
 * the baseline prior.
 *
 * Units: axial hex distance of the Voronoi cell's seed-point from origin.
 * With CELL_SCALE_L = 32, one cell radius = 32 hexes, so the thresholds
 * correspond roughly to 2 cells (inner) and 6 cells (outer) from origin.
 */
export const BIOME_PRIOR_NEAR_DIST = 64;
export const BIOME_PRIOR_FAR_DIST = 192;

/**
 * Inner prior (d < BIOME_PRIOR_NEAR_DIST): near-origin cells lean heavily
 * toward plain and forest — the world starts legible and tame.
 */
export const BIOME_PRIOR_INNER: Readonly<Record<Biome, number>> = {
  plain: 6,
  forest: 4,
  lake: 1,
  snow: 1,
  mountain: 1,
};

/**
 * Baseline prior (ADR-0013 §3): plain-biased so the world isn't dominated by
 * mountain-snow extremes when no neighbours are resolved. Used for the middle
 * distance band and as the canonical export that tests snapshot against.
 */
export const BIOME_PRIOR: Readonly<Record<Biome, number>> = {
  plain: 4,
  forest: 3,
  lake: 2,
  snow: 2,
  mountain: 2,
};

/**
 * Outer prior (d ≥ BIOME_PRIOR_FAR_DIST): deep-frontier cells stay genuinely
 * strange — variety biomes (lake/snow/mountain) still outweigh tame plain/forest
 * — but the *strangeness is carried by snow*, the one passable variety biome,
 * not by impassable lake/mountain.
 *
 * HEX-132 retune. The original outer prior (plain:2/forest:2/lake:3/snow:3/
 * mountain:3) inflated impassable-biome weight to 6/13 ≈ 46% far out. Because a
 * biome paints a whole ≈1000-hex Voronoi cell, ~46% of frontier cells became
 * near-solid impassable blocks, so a traveller threading the carved corridors
 * between them could walk far before reaching passable `ground` that hosts an
 * encounter (combat-loop scarcity — the felt symptom at distance ~1222).
 *
 * The fix shifts variety weight off impassable lake/mountain (now lake:1 +
 * mountain:2 = 3/11 ≈ 27%, down from 46%) onto **snow** — which is passable AND
 * carries a 1.4× encounter affinity (see `BIOME_AFFINITY` in ChunkedWorldGen) —
 * so the frontier reads as a cold, hostile, encounter-dense expanse rather than
 * an impassable maze. Mountain stays at 2 so distant peaks still spike the
 * skyline (HEX-127 landmarks); lake drops to 1 (impassable + least interesting).
 * The variety-≥-tame invariant (snow+lake+mountain ≥ plain+forest) still holds.
 *
 * Note: this only reduces *biome-driven* impassability. The dominant impassable
 * source far out is the base CA terrain fill (~64% of all tiles, near-flat with
 * distance — see HEX-132 diagnosis); materially lowering that is an ADR-0006
 * topology change, deliberately out of scope here.
 */
export const BIOME_PRIOR_OUTER: Readonly<Record<Biome, number>> = {
  plain: 2,
  forest: 3,
  lake: 1,
  snow: 3,
  mountain: 2,
};

/**
 * Per-biome evil weight (ADR-0018 §5): additive input to the Evil Pressure
 * `corruption` term. Higher values mean this biome is intrinsically more
 * corrupt and harder to keep liberated. Unconsumed by HEX-118 — Wave-3 Evil
 * Pressure reads it. Values in [0, 1].
 *
 *   plain / forest  — tame, low corruption
 *   lake            — slightly unsettled; water edges breed malice
 *   snow            — cold and isolating; moderate corruption
 *   mountain        — ancient, brooding; highest corruption
 */
export const EVIL_WEIGHT: Readonly<Record<Biome, number>> = {
  plain: 0.1,
  forest: 0.1,
  lake: 0.2,
  snow: 0.4,
  mountain: 0.5,
};

/** Select the biome prior for the given cell-centre distance from origin. */
function priorForDistance(cellOriginDist: number): Readonly<Record<Biome, number>> {
  if (cellOriginDist < BIOME_PRIOR_NEAR_DIST) return BIOME_PRIOR_INNER;
  if (cellOriginDist < BIOME_PRIOR_FAR_DIST) return BIOME_PRIOR;
  return BIOME_PRIOR_OUTER;
}

/** Salt for the cell-biome sampling PRNG; kept distinct from the jitter +
 * level salts in `biome.ts` so adding a new per-cell stream later doesn't
 * shift biome assignments. */
const BIOME_SAMPLE_SALT = 0xba0e_d0a1;

/**
 * Sample a biome for a cell given its already-resolved lower-level neighbour
 * biomes and the cell's approximate hex distance from origin. Deterministic in
 * `(seed, cq, cr, neighbours)` — `cellOriginDist` only selects the prior and
 * does NOT affect the RNG roll. The neighbours list is order-independent.
 */
export function sampleBiomeForCell(
  seed: number,
  cq: number,
  cr: number,
  lowerNeighbours: readonly Biome[],
  cellOriginDist: number = Number.POSITIVE_INFINITY,
): Biome {
  let weightSeed = hashCombine(seed >>> 0, BIOME_SAMPLE_SALT);
  weightSeed = hashCombine(weightSeed, cq | 0);
  weightSeed = hashCombine(weightSeed, cr | 0);
  const roll = mulberry32(weightSeed)();

  const prior = priorForDistance(cellOriginDist);

  let total = 0;
  const weights: Record<Biome, number> = {
    plain: 0,
    forest: 0,
    lake: 0,
    snow: 0,
    mountain: 0,
  };
  for (const b of BIOMES) {
    let w = prior[b];
    for (const n of lowerNeighbours) {
      w *= TRANSITION_MATRIX[b][n];
    }
    weights[b] = w;
    total += w;
  }
  const pick = roll * total;
  let acc = 0;
  for (const b of BIOMES) {
    acc += weights[b];
    if (pick < acc) return b;
  }
  // Fallthrough only on floating-point edge — last biome catches it.
  return BIOMES[BIOMES.length - 1] as Biome;
}
